import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'aegispay_push_dismissed_until';
const DISMISS_DAYS = 7;

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

/**
 * PushSetup — shows a one-time, dismissible banner inviting the user to enable
 * push notifications. Runs silently after login and does nothing if:
 *  - browser doesn't support push
 *  - user already granted / denied
 *  - user dismissed within the last 7 days
 */
const PushSetup = () => {
  const { getAuthHeaders, user } = useAuth();
  const [show, setShow] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return; // already granted or denied

    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() < dismissedUntil) return;

    // Show the prompt after a short delay so it doesn't blast on login
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, [user]);

  const handleEnable = async () => {
    setWorking(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notifications disabled. You can re-enable later from your browser settings.');
        setShow(false);
        return;
      }

      const { data } = await axios.get(`${API_URL}/api/push/vapid-public-key`);
      const reg = await navigator.serviceWorker.ready;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
      }

      const subJson = sub.toJSON();
      await axios.post(
        `${API_URL}/api/push/subscribe`,
        { ...subJson, user_agent: navigator.userAgent },
        { headers: getAuthHeaders() }
      );

      toast.success("You're all set! We'll ping you when it matters.");
      setShow(false);
    } catch (err) {
      console.error('Push subscribe failed:', err);
      toast.error('Could not enable notifications. Try again later.');
    } finally {
      setWorking(false);
    }
  };

  const handleLater = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400 * 1000));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[190] w-[92%] max-w-md"
      data-testid="push-setup-prompt"
    >
      <div className="rounded-2xl border-2 border-emerald-300 bg-white shadow-2xl p-4 flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-emerald-500 flex-shrink-0">
          <Bell size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-[#0B1F3A] text-sm">Never miss a payout</p>
          <p className="text-xs text-slate-600 font-medium mt-0.5">
            Get pinged when your invite activates, a group is ready to pay out, or a FutureLock cycle is due.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleEnable}
              disabled={working}
              className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3"
              data-testid="push-enable-btn"
            >
              {working ? 'Enabling…' : 'Enable Alerts'}
            </Button>
            <Button
              onClick={handleLater}
              variant="outline"
              className="h-9 rounded-lg text-xs px-3 font-semibold"
              data-testid="push-later-btn"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={handleLater}
          className="p-1 -mt-1 -mr-1 text-slate-400 hover:text-slate-600"
          aria-label="Dismiss"
          data-testid="push-dismiss-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default PushSetup;
